import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/common/EmptyState';
import { ServiceIconBox } from '@/components/common/ServiceIcon';
import { useServices, useDeleteService, useUpdateService } from '@/hooks/useServices';
import { ServiceEditor } from './components/ServiceEditor';
import { useTranslation } from '@/features/i18n';
import type { Service } from '@/types/database';

export function ServicesAdminPage() {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);

  const { data: services, isLoading } = useServices({ includeUnpublished: true });
  const deleteService = useDeleteService();
  const updateService = useUpdateService();
  const { t } = useTranslation();

  const handleDelete = async () => {
    if (serviceToDelete) {
      await deleteService.mutateAsync(serviceToDelete.id);
      setDeleteModalOpen(false);
      setServiceToDelete(null);
    }
  };

  const handleTogglePublish = async (service: Service) => {
    await updateService.mutateAsync({
      id: service.id,
      status: service.status === 'published' ? 'draft' : 'published',
    });
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setEditorOpen(true);
  };

  const handleCreate = () => {
    setEditingService(null);
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingService(null);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <SEO title={t.admin.services.title} noIndex />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{t.admin.services.title}</h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {t.admin.services.subtitle}
            </p>
          </div>
          <Button onClick={handleCreate} leftIcon={<Plus className="w-4 h-4" />}>
            {t.admin.services.newService}
          </Button>
        </div>

        {/* Services list */}
        {services && services.length > 0 ? (
          <div className="space-y-3 sm:space-y-4">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card className="p-3 sm:p-4 lg:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                    <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                      <ServiceIconBox icon={service.icon} variant="primary" className="flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                          <h3 className="font-semibold text-neutral-900 dark:text-white text-sm sm:text-base truncate max-w-[150px] sm:max-w-none">
                            {service.title}
                          </h3>
                          <div className="flex flex-wrap gap-1">
                            <Badge variant={service.status === 'published' ? 'success' : 'warning'} className="text-xs">
                              {service.status === 'published' ? t.admin.common.published : t.admin.common.draft}
                            </Badge>
                            {service.is_featured && <Badge variant="primary" className="text-xs">{t.admin.common.featured}</Badge>}
                          </div>
                        </div>
                        <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2 sm:line-clamp-1">
                          {service.short_description || t.admin.common.noDescription}
                        </p>
                        <p className="text-xs text-neutral-500 mt-1 truncate">
                          /{service.slug}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-1 sm:gap-2 flex-shrink-0 border-t sm:border-t-0 pt-2 sm:pt-0 -mx-3 sm:mx-0 px-3 sm:px-0 mt-2 sm:mt-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTogglePublish(service)}
                        title={service.status === 'published' ? t.admin.common.unpublish : t.admin.common.publish}
                        className="p-2 sm:p-2"
                      >
                        {service.status === 'published' ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(service)} className="p-2 sm:p-2">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2 sm:p-2"
                        onClick={() => {
                          setServiceToDelete(service);
                          setDeleteModalOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <EmptyState
            title={t.admin.services.noServices}
            description={t.admin.services.noServicesDesc}
            action={
              <Button onClick={handleCreate} leftIcon={<Plus className="w-4 h-4" />}>
                {t.admin.services.createFirst}
              </Button>
            }
          />
        )}
      </div>

      {/* Editor modal */}
      <Modal
        isOpen={editorOpen}
        onClose={handleEditorClose}
        title={editingService ? t.admin.services.editService : t.admin.services.newService}
        size="full"
      >
        <ServiceEditor service={editingService} onClose={handleEditorClose} />
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t.admin.services.deleteService}
      >
        <div className="p-6">
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            {t.admin.common.confirmDelete.replace('{name}', serviceToDelete?.title || '')} {t.admin.common.cannotUndo}
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={deleteService.isPending}>
              {t.common.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
